import asyncRetry from 'async/retry';
import React from 'react';
import asyncEachLimit from 'async/eachLimit';
import EventEmitter from 'events';

import util from '../tools/util';
import UserStore from './user_store';

export default {
  once,
  addChangeListener,
  removeChangeListener,
  init,
  isReady,
  getVolumeDirList,
  useVolumeDirList,
  createDir,
  fetchVolumeDir,
  requestDownload,
  del,
  deleteList,
  undelete,
  undeleteList,
  findMedia,
  addMedia,
  getUrl,
  getVersionUrl,
  restoreMedia,
};

let g_mediaListMap: any = {};
let g_extraDirListMap: any = {};

const g_eventEmitter = new EventEmitter();
const CHANGE_EVENT = 'change';
function once(callback) {
  g_eventEmitter.once(CHANGE_EVENT, callback);
}
function addChangeListener(callback) {
  g_eventEmitter.on(CHANGE_EVENT, callback);
}
function removeChangeListener(callback) {
  g_eventEmitter.removeListener(CHANGE_EVENT, callback);
}
function _emit(...args) {
  g_eventEmitter.emit(CHANGE_EVENT, ...args);
}
function init(done) {
  done && done();
}
function isReady() {
  return true;
}
function getVolumeDirList(volume_id: number, path: string) {
  const key = `${volume_id}-${path}`;
  return g_mediaListMap[key];
}
function _subscribe(callback: any) {
  addChangeListener(callback);
  return () => {
    removeChangeListener(callback);
  };
}
export function useVolumeDirList(volume_id: number, path: string) {
  const _get = React.useCallback(
    () => getVolumeDirList(volume_id, path),
    [volume_id, path]
  );
  return React.useSyncExternalStore(_subscribe, _get);
}
function createDir(params: any) {
  const { volume_id, path, new_dir } = params;
  const key = `${volume_id}-${path}`;
  let old = g_mediaListMap[key];
  const dir_list = old?.dir_list;
  const dir_name = path + new_dir;
  const dir_lc = dir_name.toLowerCase();

  if (!dir_list?.find?.((d) => d.dir_name.toLowerCase() === dir_lc)) {
    if (!g_extraDirListMap[key]) {
      g_extraDirListMap[key] = [];
    }
    const extra_list = g_extraDirListMap[key];
    if (!extra_list.find((name) => name === dir_lc)) {
      extra_list.push(dir_name);
    }
    if (!old) {
      old = { media_list: [], dir_list: [] };
    }
    if (!old.dir_list) {
      old.dir_list = [];
    }
    old.dir_list.push({ dir_name, file_count: 1 });
    g_mediaListMap[key] = util.deepClone(old);
    _emit('new_dir');
  }
}
function _addExtraDirs(key, body) {
  const extra_list = g_extraDirListMap[key];
  extra_list?.forEach?.((dir_name) => {
    const dir_lc = dir_name.toLowerCase();
    if (!body.dir_list.find((d) => d.dir_name.toLowerCase() === dir_lc)) {
      body.dir_list.push({ dir_name, file_count: 1 });
    }
  });
}
function fetchVolumeDir(params: any, done?: any) {
  const { volume_id, path } = params;
  const opts = {
    url: `/api/1/media/dir`,
    query: {
      volume_id,
      prefix_path: path,
    },
  };
  UserStore.get(opts, (err, body) => {
    if (err) {
      util.errorLog('MediaStore.fetchVolumeDir: err:', err, body);
    } else {
      const key = `${volume_id}-${path}`;
      _addExtraDirs(key, body);
      const old = g_mediaListMap[key];
      if (!util.deepEqual(old, body)) {
        g_mediaListMap[key] = body;
        _emit('update_dir');
      }
    }
    done?.(err, body);
  });
}
function requestDownload(params: any, done: (err: string | null) => void) {
  const { volume_id, media_id } = params;
  const opts = {
    url: `/api/1/media/${media_id}/download`,
    query: { volume_id },
  };
  UserStore.get(opts, (err, body) => {
    if (err) {
      util.errorLog('MediaStore.requestDownload: err:', err, body);
    }
    done(err);
  });
}
function del(params: any, done: (err: string | null) => void) {
  const { volume_id, media_id } = params;
  const opts = {
    url: `/api/1/media/${media_id}`,
    query: {
      volume_id,
    },
    body: {
      is_deleted: 1,
    },
  };
  UserStore.del(opts, (err, body) => {
    if (err) {
      util.errorLog('MediaStore.del: err:', err, body);
    }
    done(err);
  });
}
function deleteList(params: any, done: (err?: string | null) => void) {
  const { volume_id, media_list, on_progress } = params;

  const total = media_list.length;
  let count = 0;
  asyncEachLimit(
    media_list,
    5,
    (media, done) => {
      del({ volume_id, media_id: media.media_id }, (err) => {
        if (!err) {
          count++;
          on_progress({ count, total });
        }
        done(err);
      });
    },
    done
  );
}
function undelete(params: any, done: (err: string | null) => void) {
  const { volume_id, media_id } = params;
  const opts = {
    url: `/api/1/media/${media_id}/undelete`,
    query: {
      volume_id,
    },
    body: {
      is_deleted: 0,
    },
  };
  UserStore.post(opts, (err, body) => {
    if (err) {
      util.errorLog('MediaStore.undelete: err:', err, body);
    }
    done(err);
  });
}
function undeleteList(params: any, done: (err?: string | null) => void) {
  const { volume_id, media_list, on_progress } = params;

  const total = media_list.length;
  let count = 0;
  asyncEachLimit(
    media_list,
    5,
    (media, done) => {
      undelete({ volume_id, media_id: media.media_id }, (err) => {
        if (!err) {
          count++;
          on_progress({ count, total });
        }
        done(err);
      });
    },
    done
  );
}
function findMedia(params, done) {
  const { volume_id, media_path } = params;
  const opts = {
    url: '/api/1/media/find',
    query: {
      volume_id,
      media_path,
    },
    retry: true,
  };
  UserStore.get(opts, (err, body) => {
    let media;
    if (err === 404) {
      err = null;
    } else if (!err) {
      media = _fixupMedia(body);
    }
    done(err, media);
  });
}
function addMedia(params, done) {
  const { volume_id, media_path, file_size, file_hash, ctime, mtime } = params;
  const opts = {
    url: `/api/1/media`,
    query: {
      volume_id,
    },
    body: {
      media_path,
      file_size,
      file_hash,
      ctime: new Date(ctime),
      mtime: new Date(mtime),
    },
    retry: true,
  };
  UserStore.post(opts, (err, body) => {
    let media;
    if (!err) {
      media = {
        media_id: body.media_id,
        media_path,
        file_size,
        file_hash,
        ctime,
        mtime,
      };
    }
    done(err, media);
  });
}
function getUrl(params, done) {
  const { volume_id, media_id, inline } = params;
  const opts = {
    url: `/api/1/media/${media_id}/download_url`,
    query: {
      volume_id,
      inline: inline ? 1 : 0,
    },
  };
  UserStore.get(opts, (err, body) => {
    if (err) {
      util.errorLog('MediaStore.getUrl: err:', err, body);
    }
    done(err, body?.url);
  });
}
function getVersionUrl(params, done) {
  const { volume_id, media_version_id, inline } = params;
  const opts = {
    url: `/api/1/media_version/${media_version_id}/download_url`,
    query: {
      volume_id,
      inline: inline ?? false,
    },
  };
  UserStore.get(opts, (err, body) => {
    if (err) {
      util.errorLog('MediaStore.getVersionUrl: err:', err, body);
    }
    done(err, body?.url);
  });
}
function restoreMedia(params, done) {
  const { volume_id, file_hash, file_size, name, ext } = params;
  const create_time = params.create_time.replace(/:/g, '-');
  const dir = params.dir.length > 1 ? params.dir : '';
  const ctime = Date.now();
  let index = 0;
  let new_path;
  asyncRetry(
    { times: 5 },
    (done) => {
      const inc = index++ > 0 ? ` ${index}` : '';
      new_path = `${dir}/${name} (${create_time})${inc}${ext}`;
      const opts = {
        volume_id,
        media_path: new_path,
        file_size,
        file_hash,
        ctime,
        mtime: ctime,
      };
      addMedia(opts, (err, body) => {
        if (err) {
          util.errorLog('MediaStore.restoreMedia: err:', err, body);
        }
        done(err);
      });
    },
    (err) => done(err, new_path)
  );
}
function _fixupMedia(body) {
  if (body && body.mtime) {
    body.mtime = Date.parse(body.mtime);
  }
  return body;
}
